import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  useTheme,
  useMediaQuery,
  Badge,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  LocationOn as LocationIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  CheckCircle as CompletedIcon,
  CheckCircle,
  RadioButtonUnchecked as PendingIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { PortalTaskCreate } from './PortalTaskCreate';
import { TaskDetailsDialog } from '../../components/TaskDetailsDialog';
import { formatLocationAddress } from '../../utils/addressFormatter';

interface Task {
  id: string;
  title: string;
  description?: string;
  statusId?: string;
  workflowStatus?: {
    id: string;
    name: string;
    color: string;
  };
  priority: string;
  createdAt: string;
  updatedAt?: string;
  dueDate?: string;
  assignee?: {
    name: string;
  };
  metadata?: {
    category?: string;
    location?: string;
    locationId?: string;
  };
  attachments?: Array<{
    id: string;
    fileName: string;
    fileUrl: string;
  }>;
}

interface PortalServiceRequestsProps {
  customerId: string;
  tenantId: string;
  customer?: any;
  categories?: Array<{
    id: string;
    name: string;
    color: string;
    description?: string;
    orderIndex?: number;
  }>;
  portalSettings?: {
    defaultWorkflowId?: string;
    defaultStageId?: string;
    defaultCategoryId?: string;
  };
  confirmationMessage?: string;
}

const getStatusIcon = (taskStatus?: { name: string; color: string }) => {
  if (!taskStatus) return <PendingIcon sx={{ fontSize: 20, color: '#94A3B8' }} />;
  
  const statusLower = taskStatus.name.toLowerCase();
  if (statusLower.includes('done') || statusLower.includes('completed') || statusLower.includes('resolved')) {
    return <CompletedIcon sx={{ fontSize: 20, color: '#10B981' }} />;
  }
  if (statusLower.includes('progress') || statusLower.includes('treating')) {
    return <PendingIcon sx={{ fontSize: 20, color: '#3B82F6' }} />;
  }
  if (statusLower.includes('blocked') || statusLower.includes('cancelled')) {
    return <WarningIcon sx={{ fontSize: 20, color: '#EF4444' }} />;
  }
  return <PendingIcon sx={{ fontSize: 20, color: taskStatus.color || '#94A3B8' }} />;
};

const getStatusColor = (status: string | undefined) => {
  if (!status) return 'default';
  const statusLower = status.toLowerCase();
  if (statusLower.includes('done') || statusLower.includes('completed') || statusLower.includes('resolved')) return 'success';
  if (statusLower.includes('progress') || statusLower.includes('treating')) return 'primary';
  if (statusLower.includes('review') || statusLower.includes('assessing')) return 'warning';
  if (statusLower.includes('cancelled') || statusLower.includes('blocked')) return 'error';
  return 'default';
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'Low': 
    case 'Lowest': return '#10B981';
    case 'Medium': return '#3B82F6';
    case 'High': 
    case 'Highest': return '#F59E0B';
    case 'Critical':
    case 'Urgent': return '#EF4444';
    default: return '#94A3B8';
  }
};

const formatStatus = (status: string | undefined) => {
  if (!status) return 'Unknown';
  return status.replace(/([A-Z])/g, ' $1').trim();
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

export const PortalServiceRequests: React.FC<PortalServiceRequestsProps> = ({ 
  customerId, 
  tenantId,
  customer,
  categories,
  portalSettings,
  confirmationMessage 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, [customerId]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const API_URL = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';
      const token = sessionStorage.getItem('portalToken');
      
      const response = await fetch(`${API_URL}/portal/tasks?customerId=${customerId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Mock data for development
        setTasks([
          {
            id: '1',
            title: 'Equipment Maintenance Request',
            description: 'Annual maintenance check needed for HVAC system',
            status: 'InProgress',
            priority: 'Medium',
            createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            metadata: {
              category: 'Maintenance',
              location: 'Main Office - 123 Main St'
            }
          },
          {
            id: '2',
            title: 'Service Inquiry',
            description: 'Need information about additional services',
            status: 'Done',
            priority: 'Low',
            createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            metadata: {
              category: 'Inquiry',
              location: 'Warehouse - 456 Industrial Ave'
            }
          },
        ]);
      } else {
        const data = await response.json();
        setTasks(data.tasks || data || []);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Unable to load service requests');
      // Mock data for development
      setTasks([
        {
          id: '1',
          title: 'Equipment Maintenance Request',
          description: 'Annual maintenance check needed for HVAC system',
          status: 'InProgress',
          priority: 'Medium',
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          metadata: {
            category: 'Maintenance',
            location: 'Main Office - 123 Main St'
          }
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskCreated = () => {
    setCreateDialogOpen(false);
    setConfirmationDialogOpen(true);
    fetchTasks();
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Service Requests List */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TaskIcon color="primary" sx={{ mr: 1, fontSize: isMobile ? 20 : 24 }} />
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="primary" sx={{ fontWeight: 600 }}>
                    Service Requests
                  </Typography>
                  <Badge 
                    badgeContent={tasks.length} 
                    color="primary" 
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                  size={isMobile ? 'small' : 'medium'}
                >
                  {isMobile ? 'New' : 'New Request'}
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {tasks.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <TaskIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Service Requests Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Create your first service request to get started
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    Create Service Request
                  </Button>
                </Box>
              ) : (
                <List sx={{ py: 0 }}>
                  {tasks.map((task, index) => (
                    <React.Fragment key={task.id}>
                      <ListItemButton 
                        onClick={() => {
                          setSelectedTask(task);
                          setDetailsDialogOpen(true);
                        }}
                        selected={false}
                        sx={{ 
                          borderRadius: 1,
                          mb: 1,
                          '&.Mui-selected': {
                            bgcolor: 'primary.50',
                            '&:hover': {
                              bgcolor: 'primary.100',
                            }
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 2 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight={500}>
                              {task.title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip 
                                label={task.taskStatus?.name || 'Unknown'} 
                                size="small" 
                                sx={{ 
                                  height: 20,
                                  backgroundColor: task.taskStatus?.color || '#94A3B8',
                                  color: '#fff'
                                }}
                              />
                              <Chip 
                                label={task.priority} 
                                size="small" 
                                sx={{ 
                                  height: 20,
                                  bgcolor: getPriorityColor(task.priority),
                                  color: 'white'
                                }}
                              />
                              {task.metadata?.category && (
                                <Typography variant="caption" color="text.secondary">
                                  {task.metadata.category}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                • {formatDate(task.createdAt)}
                              </Typography>
                            </Box>
                            {task.metadata?.location && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <LocationIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5 }} />
                                <Typography variant="caption" color="text.secondary">
                                  {task.metadata.location}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <OpenInNewIcon 
                              sx={{ 
                                fontSize: 18, 
                                color: 'action.active',
                                opacity: 0.6,
                              }} 
                            />
                          </Box>
                        </Box>
                      </ListItemButton>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Create Service Request Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Create New Service Request</Typography>
            <IconButton onClick={() => setCreateDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <PortalTaskCreate 
            customerId={customerId}
            tenantId={tenantId}
            customer={customer}
            onTaskCreated={handleTaskCreated}
            categories={categories}
            portalSettings={portalSettings}
            confirmationMessage={confirmationMessage}
          />
        </DialogContent>
      </Dialog>

      {/* Task Details Dialog */}
      <TaskDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            <Typography>Service Request Submitted</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmationMessage || 'Thank you for your service request. We\'ll review it and contact you shortly.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationDialogOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};